/**
 * Dev-only auth stand-ins, split out of cognitoAuth.ts: the no-Cognito dev
 * user/token (dev-mock) and the VITE_MOCK_COGNITO mode that routes Cognito
 * calls to the mock server's Cognito family over the Vite dev proxy
 * (/cognito → legacy-API target). Both are inert in production builds and
 * whenever a real Cognito user pool is configured — a stray flag must never
 * ship a mock login. Documented in app/.env.example.
 */

import {
  REFRESH_TOKEN_KEY,
  ACCESS_TOKEN_KEY,
  ID_TOKEN_KEY,
  CognitoTokens,
  CognitoAuthResult,
  isCognitoConfigured,
  decodeJwtPayload
} from './authShared';

// Warn at import time if the flag is set while real Cognito is configured,
// where mock-Cognito mode is inert.
if (import.meta.env.DEV && import.meta.env.VITE_MOCK_COGNITO && isCognitoConfigured()) {
  console.warn(
    '[mockCognito] VITE_MOCK_COGNITO is set, but real Cognito is also configured ' +
    '(VITE_COGNITO_USER_POOL_ID / VITE_COGNITO_CLIENT_ID). Mock-Cognito mode is ' +
    'inert. The real Cognito path is used.',
  );
}

// ── Dev-mock mode (no Cognito configured, dev-only) ────────────────────────

/**
 * Marks the dev-gated token minted by buildDevMockToken(). Mock-Cognito mode
 * uses it to reject a leftover auto-login token in localStorage, which must
 * not restore as a mock-Cognito session (the Sign In page should render
 * instead). Tokens minted by the mock's Cognito family carry no such claim.
 */
const DEV_TOKEN_CLAIM = 'custom:devToken';

/**
 * Fixed local-dev user when Cognito is not configured. Mirrors the
 * server-side dev user (server/middleware/cognitoAuth.js and zeecore's
 * LOCAL_DEV_ACTOR_ID) so the UI and API agree on who is acting.
 */
export function getDevUser(): {
  id: string;
  email: string;
  isAdmin: boolean;
  permissions: string[];
  useCases: string[];
} | null {
  if (!import.meta.env.DEV || isCognitoConfigured() || mockCognitoMode()) return null;
  return {
    id: 'dev-user',
    email: 'dev@local',
    isAdmin: true,
    permissions: [],
    useCases: ['logistics'],
  };
}

function base64UrlEncode(value: string): string {
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Dev-only stand-in for a Cognito ID token, minted by the refreshSession()
 * short-circuit in cognitoAuth.ts. Never minted in production or when
 * Cognito is configured.
 */
export function buildDevMockToken(): string {
  const header = base64UrlEncode(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const now = Math.floor(Date.now() / 1000);
  const payload = base64UrlEncode(
    JSON.stringify({
      sub: 'dev-user',
      email: 'dev@local',
      'cognito:username': 'dev@local',
      'custom:isAdmin': 'true',
      'custom:organisationId': 'org123',
      'custom:isDemo': true,
      'custom:useCases': 'logistics',
      [DEV_TOKEN_CLAIM]: 'true',
      iat: now,
      // Far-future on purpose: never validated against a real user pool.
      exp: now + 60 * 60 * 24 * 365 * 50,
    }),
  );
  return `${header}.${payload}.mock`;
}

/**
 * Pure (inputs as parameters — import.meta.env.DEV is not stubbable in the
 * app's Vitest setup) so its truth table is unit-testable.
 */
export function isDevMockAuthEnabled(isDev: boolean, isCognitoConfigured: boolean): boolean {
  return Boolean(isDev) && !isCognitoConfigured;
}

// ── Mock-Cognito mode (VITE_MOCK_COGNITO, dev-only) ─────────────────────────

/** Read at call time so the gate reflects the current environment and stays testable. */
function isMockCognitoFlagSet(): boolean {
  return Boolean(import.meta.env.VITE_MOCK_COGNITO);
}

/** Pure so its truth table is unit-testable (same style as isDevMockAuthEnabled). */
export function isMockCognitoEnabled(
  isDev: boolean,
  isMockCognitoFlagSet: boolean,
  isCognitoConfigured: boolean,
): boolean {
  return Boolean(isDev) && isMockCognitoFlagSet && !isCognitoConfigured;
}

/** The live gate for cognitoAuth.ts call sites. */
export function mockCognitoMode(): boolean {
  return isMockCognitoEnabled(import.meta.env.DEV, isMockCognitoFlagSet(), isCognitoConfigured());
}

interface MockCognitoTokenSet {
  AccessToken?: string;
  IdToken?: string;
  RefreshToken?: string;
}

interface MockCognitoBody {
  __type?: string;
  message?: string;
  ChallengeName?: string | null;
  ChallengeParameters?: Record<string, string>;
  Session?: string | null;
  AuthenticationResult?: MockCognitoTokenSet;
}

const MOCK_COGNITO_PREFIX = '/cognito';

async function mockCognitoFetch(
  path: string,
  body: Record<string, unknown>,
): Promise<{ ok: boolean; data: MockCognitoBody }> {
  const response = await fetch(`${MOCK_COGNITO_PREFIX}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await response.json().catch(() => ({}))) as MockCognitoBody;
  return { ok: response.ok, data };
}

/** Map a Cognito error body ({ __type, message }) onto an Error carrying the Cognito name. */
function mockCognitoError(data: MockCognitoBody): Error {
  const rawType = typeof data.__type === 'string' ? data.__type : 'UnknownException';
  // Cognito types are prefixed (AWS::Cognito::IdentityService.NotAuthorizedException);
  // the SDK surfaces the suffix as err.name.
  const name = rawType.includes('::') ? rawType.slice(rawType.lastIndexOf('::') + 2) : rawType;
  const error = new Error(typeof data.message === 'string' && data.message ? data.message : name);
  error.name = name;
  return error;
}

function storeMockCognitoTokens(tokens: CognitoTokens): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
  localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
}

function tokenSetFromMockCognito(data: MockCognitoBody): CognitoTokens {
  const result = data.AuthenticationResult;
  return {
    accessToken: result?.AccessToken ?? '',
    idToken: result?.IdToken ?? '',
    refreshToken: result?.RefreshToken ?? '',
  };
}

export async function mockCognitoAuthenticateUser(email: string, password: string): Promise<CognitoAuthResult> {
  const { ok, data } = await mockCognitoFetch('/initiate-auth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    Username: email,
    Password: password,
  });
  if (!ok) throw mockCognitoError(data);
  if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
    // Same pre-challenge shape as the real path (tokens null placeholder).
    return {
      tokens: null as unknown as CognitoTokens,
      challengeName: 'NEW_PASSWORD_REQUIRED',
      session: data.Session ?? undefined,
      userAttributes: {},
    };
  }
  const tokens = tokenSetFromMockCognito(data);
  storeMockCognitoTokens(tokens);
  return { tokens };
}

export async function mockCognitoCompleteNewPasswordChallenge(
  email: string,
  newPassword: string,
): Promise<CognitoAuthResult> {
  const { ok, data } = await mockCognitoFetch('/respond-to-auth-challenge', {
    Challenge: 'NEW_PASSWORD_REQUIRED',
    Username: email,
    ChallengeResponse: { NEW_PASSWORD: newPassword },
  });
  if (!ok) throw mockCognitoError(data);
  const tokens = tokenSetFromMockCognito(data);
  storeMockCognitoTokens(tokens);
  return { tokens };
}

export async function mockCognitoForgotPassword(email: string): Promise<void> {
  const { ok, data } = await mockCognitoFetch('/forget-password', { Username: email });
  if (!ok) throw mockCognitoError(data);
}

export async function mockCognitoConfirmPassword(
  email: string,
  verificationCode: string,
  newPassword: string,
): Promise<void> {
  const { ok, data } = await mockCognitoFetch('/confirm-forget-password', {
    Username: email,
    ConfirmationCode: verificationCode,
    Password: newPassword,
  });
  if (!ok) throw mockCognitoError(data);
}

/**
 * Read the stored mock-Cognito tokens (far-future expiry, no round-trip).
 * Returns null for a leftover dev auto-login token (DEV_TOKEN_CLAIM), which
 * must not restore the pre-logged-in dev user in mock-Cognito mode.
 */
export function readStoredMockCognitoSession(): CognitoTokens | null {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const idToken = localStorage.getItem(ID_TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!accessToken || !idToken || !refreshToken) return null;
  const payload = decodeJwtPayload(accessToken);
  if (payload?.[DEV_TOKEN_CLAIM] === 'true') return null;
  const exp = payload?.exp;
  if (typeof exp === 'number' && exp < Math.floor(Date.now() / 1000) + 60) return null;
  return { accessToken, idToken, refreshToken };
}
